import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { formatApiMessage } from '@/api/client';
import { documentsApi } from '@/api/documents.api';
import { type EnvelopeStatus, type EnvelopeView, envelopesApi } from '@/api/envelopes.api';
import { foldersApi } from '@/api/folders.api';
import { type NumberingElement, numberingSchemeApi } from '@/api/numbering-scheme.api';
import { useAuthStore } from '@/stores/auth.store';

/**
 * Document Registry — top-level "reyestr" view requested by the customer.
 *
 * Differs from the Library page in two ways:
 *   1. Flat table, not a tree — every existing envelope is a row with full
 *      metadata visible at a glance, ready to export to Excel for audit.
 *   2. Per-row action menu (open, download signed PDF, certificate, new
 *      revision, audit) so the registry doubles as the central operations
 *      console, not a read-only archive.
 *
 * Visibility follows the same org-wide library ACL: every employee can
 * browse and download, regardless of whether they were on the routing
 * list. Mutating actions (new revision) are gated by creator/admin.
 */
/**
 * Document Register — the full controlled-document table (filters, exports,
 * per-row actions, next-review editing).
 *
 * Rendered as a section inside the Document Library rather than as its own
 * page: the customer asked for the register and the library to be one screen.
 * It fetches its own data, so it is independent of the library's query.
 */
export function RegistrySection() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);

  const [search, setSearch] = useState('');
  const [scope, setScope] = useState<'active' | 'archive'>('active');
  const [statusFilter, setStatusFilter] = useState<EnvelopeStatus | ''>('');
  const [folderFilter, setFolderFilter] = useState<string>('');
  // "Document Naming" filter: when a scheme is selected, the register is
  // scoped to documents numbered under it AND the single Number column is
  // broken into one column per naming element (patron requirement).
  const [schemeFilter, setSchemeFilter] = useState<string>('');
  const [exporting, setExporting] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['registry', scope],
    queryFn: () =>
      envelopesApi.list({
        perPage: 500,
        library: true,
        archive: scope === 'archive',
        // Declined documents never belong in the official register — they stay
        // visible only on the dashboard's Declined tab.
        excludeDeclined: true,
      }),
  });

  const { data: folderData } = useQuery({
    queryKey: ['folders', 'list'],
    queryFn: () => foldersApi.list(),
  });

  const { data: schemeData } = useQuery({
    queryKey: ['numbering-schemes'],
    queryFn: () => numberingSchemeApi.list(),
  });

  // Columns derived from the selected scheme's naming elements. Null unless a
  // scheme with a structured `elements` array is selected — legacy raw-format
  // schemes keep the default single Number column.
  const elementCols = useMemo(() => {
    if (!schemeFilter) return null;
    const scheme = (schemeData ?? []).find((s) => s.id === schemeFilter);
    if (!scheme?.elements || scheme.elements.length === 0) return null;
    // Header for each element: the admin-given name wins; otherwise fall back
    // to the element's own code/short value ("FR", "BRR"), or "Number" for the
    // running counter (matches the patron's FR-BRR-001 → FR | BRR | Number).
    const labelOf = (el: NumberingElement): string => {
      if (el.name && el.name.trim()) return el.name.trim();
      switch (el.kind) {
        case 'code':
          return el.documentType?.trim() || el.shortName || t('registry.col_code_el', 'Code');
        case 'sequence':
          return t('registry.col_number_el', 'Number');
        case 'list':
          return t('registry.col_list_el', 'List');
        case 'input':
          return t('registry.col_input_el', 'Input');
      }
    };
    return scheme.elements.map(labelOf);
  }, [schemeFilter, schemeData, t]);

  const filtered = useMemo(() => {
    const items = data?.items ?? [];
    const q = search.trim().toLowerCase();
    // "Sent" and "In progress" are treated as a single status (patron). Both
    // map to the canonical 'in_progress' for filtering and display.
    const canon = (s: EnvelopeStatus) => (s === 'sent' ? 'in_progress' : s);
    return items.filter((e) => {
      // Declined documents are excluded from the register (belt-and-suspenders
      // alongside the server-side excludeDeclined filter).
      if (e.status === 'declined') return false;
      if (schemeFilter && e.numberingSchemeId !== schemeFilter) return false;
      if (statusFilter && canon(e.status) !== canon(statusFilter)) return false;
      if (folderFilter === 'unfiled' && e.folder) return false;
      if (folderFilter && folderFilter !== 'unfiled' && e.folder?.id !== folderFilter) return false;
      if (!q) return true;
      return (
        e.title.toLowerCase().includes(q) ||
        e.documentNumber?.toLowerCase().includes(q) ||
        e.creator.email.toLowerCase().includes(q) ||
        e.creator.firstName.toLowerCase().includes(q) ||
        e.creator.lastName.toLowerCase().includes(q) ||
        e.changeDescription?.toLowerCase().includes(q)
      );
    });
  }, [data?.items, search, statusFilter, folderFilter, schemeFilter]);

  const onExport = async (format: 'xlsx' | 'csv' | 'pdf') => {
    setExporting(true);
    try {
      const params = {
        library: true,
        archive: scope === 'archive',
        status: statusFilter || undefined,
        folder: folderFilter || undefined,
        search: search || undefined,
        excludeDeclined: true,
        numberingSchemeId: schemeFilter || undefined,
      };
      if (format === 'xlsx') await envelopesApi.exportXlsx(params);
      else if (format === 'pdf') await envelopesApi.exportPdf(params);
      else await envelopesApi.exportCsv(params);
    } catch (err) {
      toast.error(formatApiMessage(err));
    } finally {
      setExporting(false);
    }
  };

  const newRevisionMutation = useMutation({
    mutationFn: (uuid: string) => envelopesApi.newRevision(uuid),
    onSuccess: (env) => {
      qc.invalidateQueries({ queryKey: ['registry'] });
      toast.success(t('registry.revision_created'));
      navigate(`/envelopes/new?continue=${env.id}`);
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const setReviewDateMutation = useMutation({
    mutationFn: ({ uuid, date }: { uuid: string; date: string | null }) =>
      envelopesApi.setReviewDate(uuid, date),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['registry'] });
      toast.success(t('registry.review_saved'));
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const downloadSigned = async (env: EnvelopeView) => {
    const doc = env.documents[0];
    if (!doc) return;
    try {
      const base = doc.originalName.replace(/\.pdf$/i, '');
      await documentsApi.downloadSigned(doc.id, `${base}__signed.pdf`);
    } catch (err) {
      toast.error(formatApiMessage(err));
    }
  };

  const downloadCertificate = async (env: EnvelopeView) => {
    try {
      await envelopesApi.downloadCertificate(env.id, `${env.title}__certificate.pdf`);
    } catch (err) {
      toast.error(formatApiMessage(err));
    }
  };

  const canManage = (env: EnvelopeView): boolean => {
    if (!me) return false;
    if (me.role === 'owner' || me.role === 'admin') return true;
    return me.id === env.creator.id;
  };

  const dateFmt = (s: string | null) =>
    s ? new Date(s).toLocaleDateString(i18n.language) : '—';

  return (
    <>
      {/* Scope tabs + KPI counters */}
      <div className="card !p-3 mb-3 flex items-center gap-3 flex-wrap">
        <div className="flex gap-1">
          <button
            onClick={() => setScope('active')}
            className={`px-3 py-1.5 rounded-md text-[13px] font-medium ${
              scope === 'active' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700'
            }`}
          >
            📁 {t('registry.scope_active')}
          </button>
          <button
            onClick={() => setScope('archive')}
            className={`px-3 py-1.5 rounded-md text-[13px] font-medium ${
              scope === 'archive' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700'
            }`}
          >
            🗄 {t('registry.scope_archive')}
          </button>
        </div>
        <div className="ml-auto text-[12px] text-muted">
          {filtered.length} / {data?.items.length ?? 0} {t('registry.row_label')}
        </div>
      </div>

      {/* Filter bar */}
      <div className="card !p-3 mb-3 flex items-center gap-2 flex-wrap">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('registry.search_placeholder') as string}
          className="input max-w-sm text-[13px]"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as EnvelopeStatus | '')}
          className="input max-w-[180px] text-[13px]"
        >
          <option value="">{t('registry.filter_all_status')}</option>
          <option value="draft">{t('envelope.status_draft')}</option>
          <option value="in_progress">{t('envelope.status_in_progress')}</option>
          <option value="completed">{t('envelope.status_completed')}</option>
          <option value="voided">{t('envelope.status_voided')}</option>
          <option value="expired">{t('envelope.status_expired')}</option>
        </select>
        <select
          value={schemeFilter}
          onChange={(e) => setSchemeFilter(e.target.value)}
          className="input max-w-[200px] text-[13px]"
          title={t('registry.filter_naming') as string}
        >
          <option value="">{t('registry.filter_all_naming')}</option>
          {(schemeData ?? []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        <select
          value={folderFilter}
          onChange={(e) => setFolderFilter(e.target.value)}
          className="input max-w-[200px] text-[13px]"
        >
          <option value="">{t('registry.filter_all_folders')}</option>
          <option value="unfiled">{t('registry.filter_unfiled')}</option>
          {(folderData?.folders ?? []).map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </select>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => onExport('xlsx')}
            disabled={exporting || filtered.length === 0}
            className="btn btn-secondary btn-sm"
            title={t('registry.export_xlsx_hint') as string}
          >
            📊 {t('registry.export_xlsx')}
          </button>
          <button
            onClick={() => onExport('pdf')}
            disabled={exporting || filtered.length === 0}
            className="btn btn-secondary btn-sm"
            title={t('registry.export_pdf_hint') as string}
          >
            📄 {t('registry.export_pdf')}
          </button>
          <button
            onClick={() => onExport('csv')}
            disabled={exporting || filtered.length === 0}
            className="btn btn-ghost btn-sm"
          >
            CSV
          </button>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="card text-center py-12 text-muted">{t('common.loading')}</div>
      ) : filtered.length === 0 ? (
        <div className="card text-center py-12">
          <div className="text-3xl mb-2">📋</div>
          <div className="font-medium text-gray-900">{t('registry.empty_title')}</div>
          <div className="text-[13px] text-muted mt-1">{t('registry.empty_text')}</div>
        </div>
      ) : (
        <div className="card !p-0 overflow-x-auto">
          <table className="table text-[12px]">
            <thead>
              <tr>
                {elementCols
                  ? elementCols.map((label, i) => <th key={`el-${i}`}>{label}</th>)
                  : <th>{t('registry.col_number')}</th>}
                <th>{t('registry.col_title')}</th>
                <th>R</th>
                <th>{t('registry.col_status')}</th>
                {!elementCols && (
                  <>
                    <th>{t('registry.col_source')}</th>
                    <th>{t('registry.col_dept')}</th>
                    <th>{t('registry.col_form')}</th>
                  </>
                )}
                <th>{t('registry.col_document_source')}</th>
                <th>{t('registry.col_confidentiality')}</th>
                <th>{t('registry.col_folder')}</th>
                <th>{t('registry.col_creator')}</th>
                <th>{t('registry.col_created')}</th>
                <th>{t('registry.col_completed')}</th>
                <th>{t('registry.col_next_review')}</th>
                <th>{t('registry.col_change_desc')}</th>
                <th className="text-right">{t('registry.col_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((env) => (
                <tr key={env.id} className={env.archivedAt ? 'bg-warning-bg/20' : ''}>
                  {elementCols
                    ? splitDocumentNumber(env.documentNumber, elementCols.length).map((val, i) => (
                        <td key={`el-${i}`} className="font-mono text-[11px] text-gray-700 whitespace-nowrap">
                          {val || <span className="text-muted">—</span>}
                        </td>
                      ))
                    : (
                      <td className="whitespace-nowrap">
                        {env.documentNumber ? (
                          <code className="text-[11px] bg-gray-100 px-1.5 py-0.5 rounded">
                            {env.documentNumber}
                          </code>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                    )}
                  <td className="max-w-[260px]">
                    <Link
                      to={`/envelopes/${env.id}`}
                      className="font-medium text-gray-900 hover:text-primary truncate inline-block max-w-full"
                    >
                      {env.title}
                    </Link>
                  </td>
                  <td className="text-center font-medium">R{env.numberingRevision}</td>
                  <td>
                    <StatusPill status={env.status} archived={!!env.archivedAt} />
                  </td>
                  {!elementCols && (
                    <>
                      <td className="font-mono text-[11px] text-gray-700">{env.numberingType ?? '—'}</td>
                      <td className="font-mono text-[11px] text-gray-700">{env.numberingDept ?? '—'}</td>
                      <td className="font-mono text-[11px] text-gray-700">{env.numberingForm ?? '—'}</td>
                    </>
                  )}
                  <td className="text-[11px] text-gray-700">{env.documentSource ?? '—'}</td>
                  <td className="text-[11px] text-gray-700">{env.confidentiality ?? '—'}</td>
                  <td>
                    {env.folder ? (
                      <span className="inline-flex items-center gap-1 text-[11px]">
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ background: env.folder.color }}
                        />
                        {env.folder.name}
                      </span>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td className="text-[11px] text-gray-700 max-w-[160px] truncate">
                    {env.creator.firstName} {env.creator.lastName}
                  </td>
                  <td className="text-[11px] text-muted whitespace-nowrap">
                    {dateFmt(env.createdAt)}
                  </td>
                  <td className="text-[11px] text-muted whitespace-nowrap">
                    {dateFmt(env.completedAt)}
                  </td>
                  <td className="text-[11px] whitespace-nowrap">
                    <ReviewDateCell
                      env={env}
                      canEdit={canManage(env)}
                      saving={setReviewDateMutation.isPending}
                      onSave={(uuid, date) => setReviewDateMutation.mutate({ uuid, date })}
                    />
                  </td>
                  <td className="text-[11px] text-gray-700 max-w-[280px]">
                    {env.changeDescription ? (
                      <span title={env.changeDescription} className="line-clamp-2">
                        {env.changeDescription}
                      </span>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td className="text-right whitespace-nowrap">
                    <ActionMenu
                      env={env}
                      canManage={canManage(env)}
                      onOpen={() => navigate(`/envelopes/${env.id}`)}
                      onDownloadSigned={() => downloadSigned(env)}
                      onDownloadCertificate={() => downloadCertificate(env)}
                      onNewRevision={() => {
                        if (confirm(t('registry.confirm_revision', { rev: env.numberingRevision + 1 }))) {
                          newRevisionMutation.mutate(env.id);
                        }
                      }}
                      revisionPending={newRevisionMutation.isPending}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/**
 * Split a compiled document number into its naming-element parts. The numbering
 * engine joins element segments with "-", so a positional split lines each
 * segment up with the scheme's elements (e.g. "FR-BRR-001" → ["FR","BRR","001"]).
 * Padded/truncated to `count` so the row always matches the header column count.
 */
function splitDocumentNumber(documentNumber: string | null, count: number): string[] {
  const parts = documentNumber ? documentNumber.split('-') : [];
  return Array.from({ length: count }, (_, i) => parts[i] ?? '');
}

/**
 * Next-review-date cell. Two modes driven by reviewDateSource:
 *   • 'algebra' → mirrored (read-only) from the linked Algebra DOC row, shown
 *     with an "Algebra" badge so the owner knows where to edit it.
 *   • else → Documentology-native; editable inline (creator/admin only) via a
 *     date input. Dates are handled as bare YYYY-MM-DD to stay timezone-safe.
 */
function ReviewDateCell({
  env,
  canEdit,
  saving,
  onSave,
}: {
  env: EnvelopeView;
  canEdit: boolean;
  saving: boolean;
  onSave: (uuid: string, date: string | null) => void;
}) {
  const { t } = useTranslation();
  const value = env.nextReviewDate ? env.nextReviewDate.slice(0, 10) : '';

  if (env.reviewDateSource === 'algebra') {
    return (
      <span className="inline-flex items-center gap-1">
        <span className="text-gray-700">{value || '—'}</span>
        <span
          title={t('registry.review_from_algebra') as string}
          className="text-[9px] uppercase tracking-wide bg-primary/10 text-primary rounded px-1 py-0.5"
        >
          Algebra
        </span>
      </span>
    );
  }

  if (!canEdit) {
    return <span className="text-gray-700">{value || '—'}</span>;
  }

  return (
    <input
      type="date"
      value={value}
      disabled={saving}
      onChange={(e) => onSave(env.id, e.target.value || null)}
      title={t('registry.review_edit_hint') as string}
      className="input !py-0.5 !px-1.5 text-[11px] w-[135px]"
    />
  );
}

function StatusPill({ status, archived }: { status: EnvelopeStatus; archived: boolean }) {
  const { t } = useTranslation();
  const cls = archived
    ? 'bg-gray-100 text-gray-600'
    : status === 'completed'
    ? 'bg-success/10 text-success'
    : status === 'declined' || status === 'voided' || status === 'expired'
    ? 'bg-danger/10 text-danger'
    : status === 'draft'
    ? 'bg-gray-100 text-gray-700'
    : 'bg-warning-bg text-warning-fg';
  return (
    <span className={`text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 ${cls}`}>
      {archived ? t('registry.status_archived') : t(`envelope.status_${status}`)}
    </span>
  );
}

function ActionMenu({
  env,
  canManage,
  onOpen,
  onDownloadSigned,
  onDownloadCertificate,
  onNewRevision,
  revisionPending,
}: {
  env: EnvelopeView;
  canManage: boolean;
  onOpen: () => void;
  onDownloadSigned: () => void;
  onDownloadCertificate: () => void;
  onNewRevision: () => void;
  revisionPending: boolean;
}) {
  const { t } = useTranslation();
  const canRevision = canManage && env.status === 'completed' && !env.archivedAt;
  return (
    <div className="inline-flex items-center gap-0.5">
      <IconBtn onClick={onOpen} title={t('registry.action_open')}>
        ↗
      </IconBtn>
      {env.documents[0] && (
        <IconBtn onClick={onDownloadSigned} title={t('registry.action_download')}>
          ⬇
        </IconBtn>
      )}
      {env.status === 'completed' && (
        <IconBtn onClick={onDownloadCertificate} title={t('registry.action_certificate')}>
          🏅
        </IconBtn>
      )}
      <Link
        to={`/envelopes/${env.id}#audit`}
        className="w-7 h-7 rounded text-[14px] flex items-center justify-center text-gray-700 hover:bg-gray-100"
        title={t('registry.action_audit')}
      >
        🧾
      </Link>
      {canRevision && (
        <IconBtn
          onClick={onNewRevision}
          disabled={revisionPending}
          title={t('registry.action_revision', { rev: env.numberingRevision + 1 })}
        >
          ✎R{env.numberingRevision + 1}
        </IconBtn>
      )}
    </div>
  );
}

function IconBtn({
  children,
  onClick,
  disabled,
  title,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="w-7 h-7 rounded text-[12px] flex items-center justify-center text-gray-700 hover:bg-gray-100 disabled:opacity-40"
    >
      {children}
    </button>
  );
}
